import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { X, FileSpreadsheet, FileText, Download } from 'lucide-react';
import client from '@/api/client';

interface ExportField {
  key: string;
  label: string;
  group: string;
}

interface Props {
  hostelId: string;
  /** Current status filter on the tenants page — scopes the export to what's on screen ('' = all). */
  status: string;
  onClose: () => void;
  accent?: 'teal' | 'violet';
}

const PRESET_PAYMENT_TRACKING = [
  'FULL_NAME', 'PHONE', 'ROOM_NUMBER', 'BED_LABEL', 'STATUS',
  'JOINING_DATE', 'MONTHLY_RENT', 'SECURITY_DEPOSIT', 'DEPOSIT_REFUNDED',
];

const PRESET_VERIFICATION = [
  'FULL_NAME', 'PHONE', 'EMAIL', 'GENDER', 'DATE_OF_BIRTH',
  'PERMANENT_ADDRESS', 'CITY', 'STATE', 'PINCODE',
  'GOV_ID_TYPE', 'GOV_ID_NUMBER',
  'EMERGENCY_CONTACT_NAME', 'EMERGENCY_CONTACT_PHONE', 'EMERGENCY_CONTACT_REL',
  'FATHER_NAME', 'FATHER_CONTACT', 'MOTHER_NAME', 'MOTHER_CONTACT',
  'ROOM_NUMBER', 'JOINING_DATE', 'STATUS',
];

const DEFAULT_SELECTION = [
  'FULL_NAME', 'PHONE', 'EMAIL', 'ROOM_NUMBER', 'STATUS', 'JOINING_DATE', 'MONTHLY_RENT',
];

async function downloadFile(url: string, body: unknown, filename: string) {
  const blob = await client.post(url, body, { responseType: 'blob' }) as unknown as Blob;
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

export function TenantExportDialog({ hostelId, status, onClose, accent = 'teal' }: Props) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Set<string>>(new Set(DEFAULT_SELECTION));
  const [downloading, setDownloading] = useState<'excel' | 'pdf' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: fields = [], isLoading } = useQuery<ExportField[]>({
    queryKey: ['tenant-export-fields'],
    queryFn: () => client.get('/api/reports/tenants/export-fields').then(r => r as unknown as ExportField[]),
    staleTime: Infinity,
  });

  const groups = Array.from(new Set(fields.map(f => f.group)));

  function toggle(key: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function applyPreset(keys: string[]) {
    setSelected(new Set(keys));
  }

  const accentClasses = accent === 'violet'
    ? { text: 'text-violet-700', bg: 'bg-violet-600 hover:bg-violet-700', ring: 'accent-violet-600', chip: 'border-violet-300 text-violet-700 hover:bg-violet-50' }
    : { text: 'text-teal-700', bg: 'bg-teal-600 hover:bg-teal-700', ring: 'accent-teal-600', chip: 'border-teal-300 text-teal-700 hover:bg-teal-50' };

  async function handleDownload(format: 'excel' | 'pdf') {
    if (selected.size === 0) {
      setError(t('export.selectAtLeastOne', 'Select at least one field to export.'));
      return;
    }
    setDownloading(format);
    setError(null);
    try {
      await downloadFile(
        `/api/reports/tenants/export?format=${format}`,
        { hostelId, status: status || null, fields: Array.from(selected) },
        `tenants.${format === 'pdf' ? 'pdf' : 'xlsx'}`,
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDownloading(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-chrome-page rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <Download size={17} className={accentClasses.text} />
            <h2 className="text-base font-semibold text-gray-900">{t('export.title', 'Export Tenants')}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          {/* Presets */}
          <div>
            <p className="text-xs font-medium text-gray-500 mb-2">{t('export.quickPresets', 'Quick presets')}</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => applyPreset(PRESET_PAYMENT_TRACKING)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${accentClasses.chip}`}
              >
                {t('export.presetPayment', 'Payment Tracking')}
              </button>
              <button
                onClick={() => applyPreset(PRESET_VERIFICATION)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${accentClasses.chip}`}
              >
                {t('export.presetVerification', 'Full Details (Verification)')}
              </button>
              <button
                onClick={() => applyPreset(fields.map(f => f.key))}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-300 text-gray-600 hover:bg-gray-50 transition"
              >
                {t('export.selectAll', 'Select All')}
              </button>
              <button
                onClick={() => applyPreset([])}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-300 text-gray-600 hover:bg-gray-50 transition"
              >
                {t('export.clearAll', 'Clear')}
              </button>
            </div>
          </div>

          {/* Field checklist */}
          {isLoading ? (
            <p className="text-sm text-gray-400 py-6 text-center">{t('common.loading')}</p>
          ) : (
            <div className="space-y-3">
              {groups.map(group => (
                <div key={group}>
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{group}</p>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                    {fields.filter(f => f.group === group).map(f => (
                      <label key={f.key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selected.has(f.key)}
                          onChange={() => toggle(f.key)}
                          className={`w-3.5 h-3.5 ${accentClasses.ring}`}
                        />
                        {f.label}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-gray-400">
            {t('export.fieldCountSelected', '{{count}} of {{total}} fields selected', { count: selected.size, total: fields.length })}
          </p>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        {/* Actions */}
        <div className="flex gap-2 px-5 py-4 border-t border-gray-100 shrink-0">
          <button
            onClick={onClose}
            className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={() => handleDownload('excel')}
            disabled={downloading !== null}
            className={`flex-1 ${accentClasses.bg} disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition flex items-center justify-center gap-2`}
          >
            <FileSpreadsheet size={14} />
            {downloading === 'excel' ? t('export.downloading', 'Downloading…') : t('export.excel', 'Excel')}
          </button>
          <button
            onClick={() => handleDownload('pdf')}
            disabled={downloading !== null}
            className={`flex-1 ${accentClasses.bg} disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition flex items-center justify-center gap-2`}
          >
            <FileText size={14} />
            {downloading === 'pdf' ? t('export.downloading', 'Downloading…') : t('export.pdf', 'PDF')}
          </button>
        </div>
      </div>
    </div>
  );
}
